import { useCallback, useMemo, useState } from 'react';

import type { BriefLineOut, BriefOut, BriefPatch } from './api';

/**
 * Unsaved changes to a draft, held here until Save (or Send) writes them.
 *
 * Only what the user changed is held: lines removed (by id), and the needed-by
 * date and notes once edited. Everything else is read from `brief` as it is
 * now, so a refresh of the brief while it is open (someone else's save, a
 * refetch on focus) shows through in every field the user hasn't touched,
 * rather than being taken for an edit and written back over.
 */
export function useDraftEdits(brief: BriefOut) {
  const [removedLineIds, setRemovedLineIds] = useState<ReadonlySet<string>>(() => new Set());
  // null: untouched, so whatever the brief says now.
  const [neededByEdit, setNeededByEdit] = useState<string | null>(null);
  const [notesEdit, setNotesEdit] = useState<string | null>(null);

  const savedNeededBy = brief.needed_by ?? '';
  const savedNotes = brief.notes ?? '';
  const neededBy = neededByEdit ?? savedNeededBy;
  const notes = notesEdit ?? savedNotes;

  const lines = useMemo(
    () => brief.lines.filter((line) => !removedLineIds.has(line.id)),
    [brief.lines, removedLineIds],
  );

  const neededByChanged = neededByEdit !== null && neededByEdit !== savedNeededBy;
  const notesChanged = notesEdit !== null && notesEdit.trim() !== savedNotes.trim();
  const isDirty = removedLineIds.size > 0 || neededByChanged || notesChanged;

  const removeLines = useCallback((toRemove: readonly BriefLineOut[]) => {
    setRemovedLineIds((current) => new Set([...current, ...toRemove.map((line) => line.id)]));
  }, []);

  /** Forget every edit: the fields show the brief as it now is. */
  const reset = useCallback(() => {
    setRemovedLineIds(new Set());
    setNeededByEdit(null);
    setNotesEdit(null);
  }, []);

  /**
   * Only what changed. Lines in particular: sending them re-checks every
   * asset, so a notes-only save of a draft holding an expired asset would be
   * refused for a change the user never made. `alsoRemove` drops lines on the
   * way out without them having been removed on screen first; `resendLines`
   * sends the line set even if it didn't change, to have it judged afresh.
   */
  const buildPatch = useCallback(
    (
      alsoRemove: readonly BriefLineOut[] = [],
      { resendLines = false }: { resendLines?: boolean } = {},
    ): BriefPatch => {
      const patch: BriefPatch = {};
      if (neededByChanged) patch.needed_by = neededBy || null;
      if (notesChanged) patch.notes = notes.trim() || null;
      const dropped = new Set(alsoRemove.map((line) => line.id));
      if (resendLines || removedLineIds.size > 0 || dropped.size > 0) {
        patch.lines = lines
          .filter((line) => !dropped.has(line.id))
          .map((line) => ({ campaign_id: line.campaign_id, asset_id: line.asset_id }));
      }
      return patch;
    },
    [lines, neededBy, neededByChanged, notes, notesChanged, removedLineIds],
  );

  return {
    lines,
    neededBy,
    setNeededBy: setNeededByEdit,
    notes,
    setNotes: setNotesEdit,
    isDirty,
    removeLines,
    reset,
    buildPatch,
  };
}
